import Admins from "@components/admins/Admins";
import Authenticated from "@components/auth";
import AdminSubHeader from "@components/common/header/SubHeader/AdminsSubHeader";
import ClientOrganizationSubHeader from "@components/common/header/SubHeader/ClientOrganizationSubHeader";
import ClientSubHeader from "@components/common/header/SubHeader/ClientsSubHeader";
import DashboardSubHeader from "@components/common/header/SubHeader/DashboardSubHeader";
import DriverSubHeader from "@components/common/header/SubHeader/DriversSubHeader";
import InvoiceSubheader from "@components/common/header/SubHeader/InvoicesSubHeader";
import PilotSubHeader from "@components/common/header/SubHeader/PilotSubheader";
import { Dashboard } from "@components/Dashboard";
import { AcceptDeliveryByClient } from "@components/Delivery/AcceptDeliveryByClient";
import DeliveryCreationConfirmed from "@components/Delivery/ConfirmedCreatedDelivery";
import DeliveryMap from "@components/Delivery/DeliveryMap";
import { DeliveriesTable } from "@components/Delivery/DeliveryTable";
import { DriverDeliveriesTable } from "@components/Driver/DriverHistoryTable";
import DriversTable from "@components/Driver/DriversTable";
import DriverMandate from "@components/Driver/Mandate";
import Help from "@components/Help";
import DeliveryInvoicesTable from "@components/Invoices/DeliveryPayment";
import SubscriptionInvoicesTable from "@components/Invoices/SubscriptionPayment";
import EditOrganization from "@components/organization/OrganizationForm";
import OrganizationTable from "@components/organization/OrganizationTable";
import { UserRank } from "@types_def/models/user.type";
import { RoutePaths } from "@utils/constants/routes";
import { Navigate, Outlet, Route, BrowserRouter as Router, Routes } from "react-router-dom";
import DeliveryOptionsTable from "@/components/Delivery/delivery-options/DeliveryOptionsTable";
import { useNotificationTitleIndicator } from "@/components/notifications/NotificationTitleHandler";
import OrganizationMembersTable from "@/components/organization/organization-members-table";
import ProfileOutlet from "@/components/Profile";
import Terms from "@/components/terms";
import CreateDeliveryDeal from "@/routes/create-delivery-deal";
import CreateDeliveryOption from "@/routes/create-delivery-option";
import EditDeliveryOption from "@/routes/edit-delivery-option";
import OffersTableRoute from "@/routes/offers-table-route";
import NotificationContainer from "@/services/Notification";
import Login from "../components/auth/Login";
import Profile from "../components/Profile/Profile";
import AcceptedDriverDetails from "./accepted-driver-details";
import BannedDriverDetails from "./banned-driver-details";
import CalendarView from "./calendar-view";
import ClientOrganization from "./client-organization";
import ClientOrganizationAddMember from "./client-organization-add-member";

import EditDeliveryV3 from "./edit-delivery-v3";
import NewDeliveryV2 from "./newDeliveryV3";
import NewDeliveryV3 from "./newDeliveryV3";
import NewOrganization from "./newOrganization";
import Notifications from "./notifications";
import PendingDriverDetails from "./pending-driver-details";
import RefusedDriverDetails from "./refused-driver-details";
import RouteBoundaryLayout from "./route-boundary-layout";
import ViewDelivery from "./view-delivery";

type Props = {
  subHeader?: React.ReactNode;
};
function AppLayout({ subHeader }: Props) {
  useNotificationTitleIndicator();
  return (
    <>
      {subHeader}
      <Outlet />
    </>
  );
}

function AppRoutes() {
  return (
    <Router>
      <Routes>
        <Route element={<RouteBoundaryLayout />}>
          <Route
            path={RoutePaths.deliveryCustomerConfirmation}
            element={<AcceptDeliveryByClient />}
          />
          <Route path={RoutePaths.driverMandate} element={<DriverMandate />} />
          <Route path={RoutePaths.login} element={<Login />} />
          <Route element={<Authenticated />}>
            <Route path={RoutePaths.client}>
              <Route element={<AppLayout />}>
                <Route path={RoutePaths.clientHelp} element={<Help />} />
              </Route>
              <Route path={RoutePaths.clientProfile} element={<ProfileOutlet />}>
                <Route path={`${RoutePaths.clientProfile}/:id`} element={<Profile />} />
              </Route>
              <Route path={RoutePaths.clientNewDelivery}>
                <Route path={`${RoutePaths.clientNewDelivery}/:type`} element={<NewDeliveryV2 />} />
                <Route
                  path={RoutePaths.deliveryCreationConfirmed}
                  element={<DeliveryCreationConfirmed />}
                />
              </Route>
              <Route
                path={`${RoutePaths.clientOrganization}`}
                element={<AppLayout subHeader={<ClientOrganizationSubHeader />} />}
              >
                <Route path={`${RoutePaths.clientOrganization}`} element={<ClientOrganization />} />
                <Route
                  path={RoutePaths.clientOrganizationAddMember}
                  element={<ClientOrganizationAddMember />}
                />
                <Route
                  path={RoutePaths.clientOrganizationListManager}
                  element={<OrganizationMembersTable memberRank={UserRank.ORGANIZATION_MANAGER} />}
                />
                <Route
                  path={RoutePaths.clientOrganizationListMember}
                  element={<OrganizationMembersTable memberRank={UserRank.ORGANIZATION_MEMBER} />}
                />
              </Route>
              <Route
                path={`${RoutePaths.clientEditDelivery}/:type/:status/:contract`}
                element={<EditDeliveryV3 />}
              />
              <Route
                path={RoutePaths.clientPilot}
                element={<AppLayout subHeader={<PilotSubHeader />} />}
              >
                <Route
                  path={`${RoutePaths.clientPilotDeliveryTable}/:status`}
                  element={<DeliveriesTable />}
                />
                <Route path={`${RoutePaths.clientCalendarView}`} element={<CalendarView />} />

                <Route path={RoutePaths.clientPilotDeliveryOngoingMap} element={<DeliveryMap />} />
                <Route path={RoutePaths.pilotageDeliveredItem} element={<ViewDelivery />} />
              </Route>

              <Route
                path={RoutePaths.client}
                element={<Navigate to={RoutePaths.clientDashboard} />}
              />
              <Route
                path={RoutePaths.clientDashboard}
                element={<AppLayout subHeader={<DashboardSubHeader />} />}
              >
                <Route path={RoutePaths.clientDashboard} element={<Dashboard />} />
              </Route>
            </Route>
            <Route path={RoutePaths.admin}>
              <Route path={`${RoutePaths.adminNewDelivery}/:type`} element={<NewDeliveryV3 />} />
              <Route
                path={RoutePaths.adminPilot}
                element={<AppLayout subHeader={<PilotSubHeader />} />}
              >
                <Route path={RoutePaths.adminEditDeal} element={<EditDeliveryV3 />} />

                <Route path={RoutePaths.adminDeliveryForm} element={<ViewDelivery />} />

                <Route
                  path={`${RoutePaths.adminPilotCreateDealRequested}/:type/:contract`}
                  element={<CreateDeliveryDeal />}
                />
                <Route
                  path={`${RoutePaths.adminPilotCreateDealConfirmed}/:type/:contract`}
                  element={<CreateDeliveryDeal />}
                />
                <Route
                  path="/admin/pilot/edit-deal/waiting-for-driver/:type/:contract"
                  element={<CreateDeliveryDeal />}
                />
                <Route
                  path="/admin/pilot/edit-deal/alert/:type/:contract"
                  element={<CreateDeliveryDeal />}
                />
                <Route
                  path={`${RoutePaths.adminPilotDeliveryTable}/:status`}
                  element={<DeliveriesTable />}
                />
                <Route path={RoutePaths.adminPilotOffers} element={<OffersTableRoute />} />
                <Route path={RoutePaths.adminPilotDeliveryOngoingMap} element={<DeliveryMap />} />
                <Route path={RoutePaths.adminPilotageDeliveredItem} element={<ViewDelivery />} />
              </Route>
              <Route
                path={RoutePaths.adminDrivers}
                element={<AppLayout subHeader={<DriverSubHeader />} />}
              >
                <Route
                  path={`${RoutePaths.adminDriversTable}/:tableRoute`}
                  element={<DriversTable />}
                ></Route>

                <Route
                  path={`${RoutePaths.adminDriversTableWaitingRequests}/:id`}
                  element={<PendingDriverDetails />}
                ></Route>
                <Route
                  path={`${RoutePaths.adminDriversTableacceptedRequests}/:id`}
                  element={<AcceptedDriverDetails />}
                ></Route>
                <Route
                  path={`${RoutePaths.adminDriversTableacceptedHistory}/:id`}
                  element={<DriverDeliveriesTable />}
                ></Route>
                <Route
                  path={`${RoutePaths.adminDriversTablerefusedRequests}/:id`}
                  element={<RefusedDriverDetails />}
                ></Route>
                <Route
                  path={`${RoutePaths.adminDriversTabledisabledDrivers}/:id`}
                  element={<BannedDriverDetails />}
                ></Route>
                <Route
                  path={`${RoutePaths.adminDriversTabledisabledDriversHistory}/:id`}
                  element={<DriverDeliveriesTable />}
                ></Route>
              </Route>
              <Route path={RoutePaths.adminOptions} element={<DeliveryOptionsTable />} />
              <Route path={RoutePaths.adminNewOption} element={<CreateDeliveryOption />} />
              <Route path={RoutePaths.adminEditOption} element={<EditDeliveryOption />} />
              <Route
                path={RoutePaths.adminOrganization}
                element={<AppLayout subHeader={<ClientSubHeader />} />}
              >
                <Route index element={<OrganizationTable />} />
                <Route path={RoutePaths.adminNewOrganization} element={<NewOrganization />} />
                <Route path={RoutePaths.adminOrganizationsTable} element={<OrganizationTable />} />
                <Route
                  path={`${RoutePaths.adminOrganizationsTable}/:slug`}
                  element={<EditOrganization />}
                />
                <Route
                  path={`${RoutePaths.adminOrganizationsTable}/view-members/:organizationName/:slug`}
                  element={<OrganizationMembersTable memberRank={UserRank.ORGANIZATION_MEMBER} />}
                />
                <Route
                  path={`${RoutePaths.adminOrganizationsTable}/view-managers/:organizationName/:slug`}
                  element={<OrganizationMembersTable memberRank={UserRank.ORGANIZATION_MANAGER} />}
                />
              </Route>
              <Route
                path={RoutePaths.adminManage}
                element={<AppLayout subHeader={<AdminSubHeader />} />}
              >
                <Route
                  path={RoutePaths.adminManageAdmins}
                  element={<Admins rank={UserRank.SUPER_ADMIN} />}
                />
                <Route
                  path={RoutePaths.adminManageMaintainer}
                  element={<Admins rank={UserRank.MAINTAINER} />}
                />
                <Route
                  path={RoutePaths.adminManageSupport}
                  element={<Admins rank={UserRank.SUPPORT} />}
                />
              </Route>

              <Route
                path={RoutePaths.adminInvoices}
                element={<AppLayout subHeader={<InvoiceSubheader />} />}
              >
                <Route
                  path={`${RoutePaths.adminInvoicesDriver}/subscription`}
                  element={<SubscriptionInvoicesTable />}
                />
                <Route
                  path={`${RoutePaths.adminInvoicesDriver}/delivery`}
                  element={<DeliveryInvoicesTable />}
                />
                {/* <Route
                path={RoutePaths.adminInvoicesClient}
                element={<SubscriptionInvoicesTable />}
              /> */}
              </Route>
              <Route path={RoutePaths.adminTerms} element={<Terms />}></Route>
              <Route path={RoutePaths.adminNotifications} element={<Notifications />}></Route>
              <Route
                path={RoutePaths.admin}
                element={<Navigate to={RoutePaths.adminDashboard} />}
              />
              <Route
                path={RoutePaths.adminDashboard}
                element={<AppLayout subHeader={<DashboardSubHeader />} />}
              >
                <Route path={RoutePaths.adminDashboard} element={<Dashboard />} />
              </Route>
            </Route>
          </Route>
          <Route path="/" element={<Navigate to={RoutePaths.login} />} />

          {/* <Route path="*" element={<AppLayout />}>
          <Route path="*" element={<Dashboard />} />
        </Route> */}
        </Route>
      </Routes>
      <NotificationContainer />
    </Router>
  );
}

export default AppRoutes;
